import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar_old";
import Footer from "./Footer";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import axios from "axios";

const LikedProfiles = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  // Logged in user
  const loggedUser = JSON.parse(localStorage.getItem("currentUser")) || null;

  const currentUser = {
    id: loggedUser?._id || 0,
    fullName: loggedUser?.full_name || loggedUser?.name || "Guest",
    canChat: loggedUser?.can_chat || false,
    canCall: loggedUser?.can_call || false,
  };

  useEffect(() => {
    if (!loggedUser) {
      alert("User not found! Please login again.");
      navigate("/login");
    }
  }, [loggedUser, navigate]);

  const defaultAvatar =
    "https://cdn-icons-png.flaticon.com/512/149/149071.png";

  // State
  const [likedProfiles, setLikedProfiles] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // Fetch liked profiles
  useEffect(() => {
    const syncLikes = () => {
      const stored = JSON.parse(localStorage.getItem("likedProfiles")) || [];
      setLikedProfiles(stored);
    };
    syncLikes();
    window.addEventListener("storage", syncLikes);
    return () => window.removeEventListener("storage", syncLikes);
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [messages]);

  // Generate consistent chat key
  const getChatKey = (u1, u2) => {
    const sorted = [String(u1.id), String(u2.id)].sort();
    return `chat_${sorted[0]}_${sorted[1]}`;
  };

  // Open chat
  const openChat = (profile) => {
    if (!currentUser.canChat) {
      alert("🚫 Chat access denied! Upgrade your membership.");
      navigate("/membership");
      return;
    }
    setActiveChat(profile);
    const key = getChatKey(currentUser, profile);
    const oldMessages = JSON.parse(localStorage.getItem(key)) || [];
    setMessages(oldMessages);
  };

  // Send message
  const sendMessage = () => {
    if (!text.trim() || !activeChat) return;

    const key = getChatKey(currentUser, activeChat);
    const newMsg = {
      senderId: currentUser.id,
      text,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
    const updated = [...messages, newMsg];
    setMessages(updated);
    localStorage.setItem(key, JSON.stringify(updated));
    setText("");
  };

  // Call user
  const callUser = (profile) => {
    if (!currentUser.canCall) {
      alert("🚫 Call access denied! Upgrade your membership.");
      navigate("/membership");
      return;
    }
    if (!profile.mobile) {
      alert("❌ Mobile number not available.");
      return;
    }
    window.location.href = `tel:${profile.mobile}`;
  };

  // Toggle like/unlike
  const toggleLike = async (profile) => {
    const alreadyLiked = likedProfiles.some((p) => p.id === profile.id);
    let updatedLikes;

    if (alreadyLiked) {
      updatedLikes = likedProfiles.filter((p) => p.id !== profile.id);
    } else {
      const fullProfile = {
        id: profile.id,
        fullName: profile.fullName,
        state: profile.state,
        city: profile.city,
        gender: profile.gender,
        mobile: profile.mobile,
        avatar: profile.avatar,
        canChat: profile.canChat,
        canCall: profile.canCall,
      };
      updatedLikes = [...likedProfiles, fullProfile];

      try {
        await axios.post("https://rishtey.rishteymatch.in/api2/api/send-like", {
          fromUserId: currentUser.id,
          toUserId: profile.id,
        });
      } catch (err) {
        console.error("Like notification error:", err);
      }
    }

    setLikedProfiles(updatedLikes);
    localStorage.setItem("likedProfiles", JSON.stringify(updatedLikes));
  };

  // Fullscreen image modal
  const ImageModal = ({ imageUrl, onClose }) => (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
      style={{ zIndex: 2000 }}
      onClick={onClose}
    >
      <img
        src={imageUrl}
        alt="Profile"
        className="rounded shadow-lg"
        style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
        onClick={(e) => e.stopPropagation()}
      />
      <button
        onClick={onClose}
        className="btn btn-light position-absolute top-0 end-0 m-3"
        style={{ fontSize: "1.2rem", borderRadius: "50%" }}
      >
        ✕
      </button>
    </div>
  );

  // Chat screen
  if (activeChat)
    return (
      <div className="d-flex flex-column vh-100 bg-light">
        <div className="bg-dark text-white d-flex align-items-center p-3 shadow-sm">
          <button className="btn btn-dark me-3" onClick={() => setActiveChat(null)}>
            <i className="bi bi-arrow-left"></i>
          </button>
          <img
            src={activeChat.avatar || defaultAvatar}
            alt={activeChat.fullName}
            className="rounded-circle me-2 border border-2 border-white"
            width="45"
            height="45"
            onClick={() => setFullscreenImage(activeChat.avatar || defaultAvatar)}
            style={{ cursor: "pointer" }}
          />
          <div>
            <strong>{activeChat.fullName}</strong>
            <div className="text-success" style={{ fontSize: "0.85rem" }}>
              🟢 Active Now
            </div>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto p-3" style={{ background: "#f0f2f5" }}>
          {messages.map((msg, i) => (
            <div
              key={i}
              className={`d-flex mb-2 ${
                msg.senderId === currentUser.id ? "justify-content-end" : "justify-content-start"
              }`}
            >
              <div
                className={`p-2 rounded-3 shadow-sm ${
                  msg.senderId === currentUser.id ? "bg-warning text-dark" : "bg-white text-dark"
                }`}
                style={{ maxWidth: "70%" }}
              >
                <div>{msg.text}</div>
                <small className="d-block text-end" style={{ fontSize: "10px" }}>
                  {msg.time}
                </small>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="p-2 bg-white d-flex align-items-center border-top shadow-sm">
          <input
            type="text"
            className="form-control rounded-pill me-2"
            placeholder="Type a message..."
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <button
            className="btn btn-warning rounded-circle"
            style={{ width: "45px", height: "45px" }}
            onClick={sendMessage}
          >
            <i className="bi bi-send-fill"></i>
          </button>
        </div>
        {fullscreenImage && <ImageModal imageUrl={fullscreenImage} onClose={() => setFullscreenImage(null)} />}
      </div>
    );

  // Liked profiles list screen
  return (
    <>
      <Navbar />
      <div className="bg-dark" style={{ minHeight: "100vh" }}>
        <div className="container py-4">
          <button className="btn btn-light btn-sm mb-3" onClick={() => navigate(-1)}>
            ⬅ Back
          </button>
          <h5 className="fw-bold mb-3 text-white">Liked Profiles</h5>

          {likedProfiles.length === 0 ? (
            <p className="text-white text-center">No liked profiles yet!</p>
          ) : (
            <div className="row g-3">
              {likedProfiles.map((profile) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={profile.id}>
                  <div className="p-3 rounded shadow-sm text-white profile-card">
                    <div className="d-flex align-items-center mb-2 justify-content-between">
                      <div className="d-flex align-items-center">
                        <img
                          src={profile.avatar || defaultAvatar}
                          alt={profile.fullName}
                          className="rounded-circle me-3 border border-2 border-white"
                          width="50"
                          height="50"
                          onClick={() => setFullscreenImage(profile.avatar || defaultAvatar)}
                          style={{ cursor: "pointer" }}
                        />
                       <div className="profile-info">
  <strong className="profile-name">
    {profile.fullName} <span className="heart">❤️</span>
  </strong>
  <p className="profile-location">
    {profile.city}, {profile.state}
  </p>
</div>

                      </div>

                      <button
  className={`like-btn ${
    likedProfiles.some((p) => p.id === profile.id) ? "liked" : ""
  }`}
  onClick={() => toggleLike(profile)}
>
  ❤️
</button>

                    </div>

                    <div className="d-flex gap-2 mt-2">
                      <button className="btn btn-sm flex-grow-1 btn-warning" onClick={() => callUser(profile)}>
                        📞 Call
                      </button>
                      {/* <button className="btn btn-sm flex-grow-1 btn-success" onClick={() => openChat(profile)}>
                        💬 Message
                      </button> */}
                      <button
                        className="btn btn-sm flex-grow-1 btn-info"
                        onClick={() => navigate(`/moredetail/${profile.id}`, { state: profile })}
                      >
                        🔍 More Details
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {fullscreenImage && <ImageModal imageUrl={fullscreenImage} onClose={() => setFullscreenImage(null)} />}
      <Footer />
    </>
  );
};

export default LikedProfiles;
